/**
 * AIImportReviewStep
 * Tabbed review surface for a job in 'completed_pending_review'. The user
 * can edit the deal name, browse extracted properties, resolve any review
 * questions, override file classifications (which captures feedback for
 * the learning loop), then click Apply.
 *
 * Tabs: Deal · Properties · Questions · Documents · Comps · Contacts.
 * Buy-box badges and full diff display are still to come.
 */

import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Sparkles, Building2, RefreshCw, FileText, TrendingUp, Users, Loader2, Undo2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Input } from '@/components/ui/Input';
import {
  useAIImportJob,
  useAIImportJobFiles,
  useAIImportQuestions,
  useUpdateImportPlan,
  useResolveQuestion,
  useCancelAIImportJob,
  useRecordFeedback,
} from '@/hooks/useAIImport';
import { useAuthStore } from '@/stores/authStore';
import { useMicrosoftAuth } from '@/hooks/useMicrosoftAuth';
import { PropertyMapThumbnail } from '../shared/PropertyMapThumbnail';
import { PropertyStarCell } from '../shared/PropertyStarCell';
import { planPropertyStarKey, type CandidateRatingMap } from '../candidateRatings';
import { dropPlanProperties, foldAnswerIntoPlan } from '@/lib/aiImportPlan';
import { QuestionRow } from '@/components/ai-import/QuestionRow';
import { StatChip } from '../shared/StatChip';
import type { AIImportPlan, AIImportPlanContact, AIImportQuestion } from '@/types/ai-import';

interface AIImportReviewStepProps {
  jobId: string;
  /** Held candidate star previews, keyed by `planPropertyStarKey`. */
  candidateRatings?: CandidateRatingMap;
  onContinue: (plan: AIImportPlan) => void;
  onClose: () => void;
}

const fmtMoney = (n?: number | null): string | null =>
  typeof n === 'number' ? `$${Math.round(n).toLocaleString()}` : null;

/**
 * One editable field on an extracted property. Emptying the box clears the key
 * rather than writing null: ai-import-apply passes null straight through to the
 * column, so a blanked field would ERASE a value on an update instead of
 * leaving it alone.
 */
function PlanPropertyField({
  label,
  value,
  onChange,
  className,
  placeholder,
  numeric,
}: {
  label: string;
  value: string | number | undefined;
  onChange: (value: string | number | undefined) => void;
  className?: string;
  placeholder?: string;
  numeric?: boolean;
}) {
  return (
    <label className={cn('block min-w-0', className)}>
      <span className="block text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {label}
      </span>
      <Input
        value={value ?? ''}
        inputMode={numeric ? 'numeric' : undefined}
        placeholder={placeholder}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw.trim() === '') return onChange(undefined);
          if (!numeric) return onChange(raw);
          const n = Number(raw.replace(/[^0-9.]/g, ''));
          onChange(Number.isFinite(n) ? n : undefined);
        }}
        className="h-8 py-1 text-sm"
      />
    </label>
  );
}

// Cap rate / occupancy may arrive as a fraction (0.06, 0.95) or already as a
// percent (6, 95). Treat <= 1 as a fraction so both render sensibly.
const fmtPct = (n?: number | null): string | null =>
  typeof n === 'number' ? `${(n <= 1 ? n * 100 : n).toFixed(1)}%` : null;

// Turn a raw ai_import_job_files.extraction_errors string into a short, human
// reason + whether it's an upstream AI-service failure (usage limit / API error),
// so the review UI can explain WHY a file failed instead of showing "no confidence".
function summarizeImportError(raw: string): { short: string; isApiIssue: boolean; isUsageLimit: boolean } {
  const isUsageLimit = /reached your specified|usage limit|rate.?limit|quota|\b429\b/i.test(raw);
  const isApiIssue =
    isUsageLimit || /\/messages failed|anthropic|invalid_request_error|\b(401|403|5\d\d)\b|api key/i.test(raw);
  // Prefer the upstream provider's own "message" when the error wraps a JSON body.
  const m = raw.match(/"message":\s*"([^"]+)"/);
  let short = m ? m[1] : raw.replace(/^\w[\w ]*failed:\s*/i, '').split(/[\n{]/)[0].trim();
  if (short.length > 180) short = `${short.slice(0, 177)}…`;
  return { short, isApiIssue, isUsageLimit };
}

export function AIImportReviewStep({
  jobId,
  candidateRatings = {},
  onContinue,
  onClose,
}: AIImportReviewStepProps) {
  const { data: job } = useAIImportJob(jobId);
  const { data: files = [] } = useAIImportJobFiles(jobId);
  const { data: questions = [] } = useAIImportQuestions(jobId);
  const { user, profile } = useAuthStore();
  const { user: msUser } = useMicrosoftAuth();
  const updatePlan = useUpdateImportPlan();
  const resolveQuestion = useResolveQuestion();
  const cancelJob = useCancelAIImportJob();
  const recordFeedback = useRecordFeedback();

  const [localPlan, setLocalPlan] = useState<AIImportPlan | null>(null);
  const [continueError, setContinueError] = useState<string | null>(null);
  const [savingAndClosing, setSavingAndClosing] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  // Properties the user has skipped this session. Kept as a soft state (the card
  // stays rendered with an Undo) so a mis-click doesn't destroy extracted data or
  // pollute the learning loop — exclusion + property_skip feedback only commit on
  // Continue. Indices reference localPlan.properties (stable: skip doesn't splice).
  const [skipped, setSkipped] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (job?.summary && !localPlan) {
      setLocalPlan(job.summary);
    }
  }, [job?.summary, localPlan]);

  const currentUserName =
    profile?.full_name || msUser?.name || user?.email || msUser?.email || 'Unknown';

  const pendingQuestions = useMemo(
    () => questions.filter((q) => q.status === 'pending'),
    [questions],
  );

  if (!job || !localPlan) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-sm text-gray-500 dark:text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin text-primary-500" />
        Loading plan…
      </div>
    );
  }

  const setDealField = (field: keyof AIImportPlan['deal'], value: unknown) => {
    setLocalPlan((prev) => (prev ? { ...prev, deal: { ...prev.deal, [field]: value } } : prev));
  };

  const togglePrimary = (idx: number) => {
    setLocalPlan((prev) =>
      prev
        ? {
            ...prev,
            properties: prev.properties.map((p, i) => ({
              ...p,
              is_primary: i === idx,
            })),
          }
        : prev,
    );
  };

  // Edit a field on an extracted property (e.g. the name, when confirming which
  // of two merged same-property extractions is the canonical one).
  const setPropertyField = (idx: number, field: string, value: unknown) => {
    setLocalPlan((prev) =>
      prev
        ? {
            ...prev,
            properties: prev.properties.map((p, i) =>
              i === idx
                ? { ...p, fields_to_apply: { ...p.fields_to_apply, [field]: value } }
                : p,
            ),
          }
        : prev,
    );
  };

  const setContactField = (idx: number, field: keyof AIImportPlanContact, value: unknown) => {
    setLocalPlan((prev) =>
      prev
        ? {
            ...prev,
            contacts: (prev.contacts ?? []).map((c, i) =>
              i === idx ? { ...c, [field]: value } : c,
            ),
          }
        : prev,
    );
  };

  const removeContact = (idx: number) => {
    setLocalPlan((prev) =>
      prev ? { ...prev, contacts: (prev.contacts ?? []).filter((_, i) => i !== idx) } : prev,
    );
  };

  // When a question is answered with a concrete value, fold it straight into the
  // plan too — ai-import-apply applies fields_to_apply directly and never reads
  // resolved questions, so a correction that only touched the question row would
  // be invisible until a worker re-assembly. This makes the fix immediate: it
  // shows on the Properties/Deal tab and applies on Continue.
  const applyAnswerToPlan = (question: AIImportQuestion, value: unknown) => {
    setLocalPlan((prev) => (prev ? foldAnswerIntoPlan(prev, question, value) : prev));
  };

  const handleContinue = async () => {
    if (!localPlan) return;
    setContinueError(null);
    try {
      // Drop the soft-skipped properties from the plan, remapping the contact
      // and subject-sale references that index into it.
      const finalPlan: AIImportPlan = dropPlanProperties(localPlan, skipped);
      // Persist any edits the user made to the plan so a background-out / Back /
      // resume sees them, and hand the exact reviewed plan to the Decision step
      // (which applies it — avoids re-reading a stale cached summary).
      await updatePlan.mutateAsync({ id: jobId, summary: finalPlan });
      // Record property_skip feedback ONLY after the plan commit succeeds — so a
      // failed-then-retried Continue can't double-write, and an undo after a
      // failed Continue can't record a skip for a property that ends up applied.
      skipped.forEach((i) => {
        const p = localPlan.properties[i];
        if (!p) return;
        recordFeedback.mutate({
          job_id: jobId,
          feedback_type: 'property_skip',
          entity_type: 'property',
          ai_value: p.fields_to_apply,
          user_value: null,
          created_by: currentUserName,
        });
      });
      setSkipped(new Set());
      onContinue(finalPlan);
    } catch (err) {
      setContinueError(err instanceof Error ? err.message : String(err));
    }
  };

  // "Save & Close" persists the user's plan edits before closing — leaving the
  // job in completed_pending_review to resume later — rather than discarding them.
  const handleSaveAndClose = async () => {
    if (!localPlan) {
      onClose();
      return;
    }
    setContinueError(null);
    setSavingAndClosing(true);
    try {
      await updatePlan.mutateAsync({ id: jobId, summary: localPlan });
      onClose();
    } catch (err) {
      setContinueError(err instanceof Error ? err.message : String(err));
      setSavingAndClosing(false);
    }
  };

  // "Discard import" — the review modal had no way to say "no" (only the list
  // page's row menu did). Sets the job to 'cancelled' and closes.
  const handleDiscard = async () => {
    setContinueError(null);
    try {
      await cancelJob.mutateAsync(jobId);
      setShowDiscardConfirm(false);
      onClose();
    } catch (err) {
      setShowDiscardConfirm(false);
      setContinueError(err instanceof Error ? err.message : String(err));
    }
  };

  const blockedByQuestions = pendingQuestions.length > 0;

  const isDatabaseMode = localPlan.import_mode === 'database';
  // Impact counts reflect what will actually apply — skipped properties excluded.
  const activeProperties = localPlan.properties.filter((_, i) => !skipped.has(i));
  const createCount = activeProperties.filter((p) => p.action === 'create').length;
  const updateCount = activeProperties.filter((p) => p.action === 'update').length;
  const docCount = files.filter((f) => !f.user_skip).length;
  const compCount = (localPlan.valuations?.length ?? 0) + (localPlan.rent_comps?.length ?? 0);
  const contactCount = localPlan.contacts?.length ?? 0;

  // Files the pipeline couldn't process (e.g. the AI service errored). Surfaced as
  // a banner so an upstream failure doesn't look like empty / no-confidence data.
  const failedFiles = files.filter((f) => f.processing_state === 'failed' && f.extraction_errors);
  const importError = failedFiles.length > 0 ? summarizeImportError(failedFiles[0].extraction_errors!) : null;

  return (
    <div className="flex flex-col h-full">
      {importError && (
        <div className="px-6 pt-4">
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 dark:border-red-800 dark:bg-red-900/30">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0 dark:text-red-400" />
            <div className="text-sm text-red-800 dark:text-red-300">
              <div className="font-semibold">
                {importError.isUsageLimit
                  ? 'AI analysis could not run — Anthropic API usage limit reached'
                  : importError.isApiIssue
                    ? 'AI analysis failed — the AI service returned an error'
                    : `${failedFiles.length} file${failedFiles.length === 1 ? '' : 's'} could not be processed`}
              </div>
              <div className="mt-0.5 text-red-700 dark:text-red-300">{importError.short}</div>
              <div className="mt-1 text-xs text-red-600 dark:text-red-400">
                {failedFiles.length} of {files.length} file{files.length === 1 ? '' : 's'} failed.{' '}
                {importError.isUsageLimit
                  ? 'Restore the limit in the Anthropic console, then re-analyze this import.'
                  : 'Resolve the issue, then re-analyze this import.'}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Impact summary */}
      <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-primary-50/60 to-white dark:border-gray-700 dark:from-primary-900/20 dark:to-gray-900">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
              <Sparkles className="h-4 w-4" />
            </span>
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                What Claude found
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Review and edit the extracted data, then continue to choose what to do with it.
              </div>
            </div>
          </div>
          {blockedByQuestions ? (
            <Badge size="sm" variant="yellow">
              {pendingQuestions.length} open question{pendingQuestions.length === 1 ? '' : 's'}
            </Badge>
          ) : (
            <Badge size="sm" variant="green">
              <span className="inline-flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Ready to apply
              </span>
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <StatChip icon={Building2} value={createCount} label={createCount === 1 ? 'property to create' : 'properties to create'} tone="emerald" />
          <StatChip icon={RefreshCw} value={updateCount} label={updateCount === 1 ? 'property to update' : 'properties to update'} tone="primary" />
          <StatChip icon={FileText} value={docCount} label={docCount === 1 ? 'document' : 'documents'} tone="slate" />
          <StatChip icon={TrendingUp} value={compCount} label={compCount === 1 ? 'comp found' : 'comps found'} tone="amber" />
          <StatChip icon={Users} value={contactCount} label={contactCount === 1 ? 'contact' : 'contacts'} tone="slate" />
        </div>
      </div>

      {/* Tabbed body */}
      <div className="px-6 py-4 flex-1 overflow-y-auto">
        <Tabs defaultValue={isDatabaseMode ? 'properties' : 'deal'}>
          <TabsList>
            {!isDatabaseMode && <TabsTrigger value="deal">Deal</TabsTrigger>}
            <TabsTrigger value="properties">
              Properties ({localPlan.properties.length})
            </TabsTrigger>
            <TabsTrigger value="questions">
              {/* Count the whole list the panel renders (pending + resolved), not
                  just pending — otherwise "Questions (0)" opens onto resolved rows. */}
              Questions ({questions.length})
            </TabsTrigger>
            <TabsTrigger value="documents">Documents ({files.length})</TabsTrigger>
            <TabsTrigger value="comps">Comps ({compCount})</TabsTrigger>
            <TabsTrigger value="contacts">Contacts ({contactCount})</TabsTrigger>
          </TabsList>

          {/* DEAL */}
          <TabsContent value="deal">
            <div className="space-y-4 max-w-2xl">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Deal name</label>
                <Input
                  value={localPlan.deal.name}
                  onChange={(e) => setDealField('name', e.target.value)}
                />
                {localPlan.deal.name_alternatives && localPlan.deal.name_alternatives.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <span className="text-gray-500 dark:text-gray-400">Suggestions:</span>
                    {localPlan.deal.name_alternatives.map((alt) => (
                      <button
                        key={alt}
                        type="button"
                        onClick={() => setDealField('name', alt)}
                        className="text-primary-700 hover:underline dark:text-primary-300"
                      >
                        {alt}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Asking price</label>
                  <Input
                    type="number"
                    value={localPlan.deal.total_asking_price ?? ''}
                    onChange={(e) =>
                      setDealField('total_asking_price', e.target.value ? Number(e.target.value) : null)
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Estimated NOI</label>
                  <Input
                    type="number"
                    value={localPlan.deal.estimated_noi ?? ''}
                    onChange={(e) =>
                      setDealField('estimated_noi', e.target.value ? Number(e.target.value) : null)
                    }
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">Notes</label>
                <textarea
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-[3px] focus:ring-primary-500/20 focus:border-primary-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                  rows={3}
                  value={localPlan.deal.notes ?? ''}
                  onChange={(e) => setDealField('notes', e.target.value)}
                />
              </div>
            </div>
          </TabsContent>

          {/* PROPERTIES */}
          <TabsContent value="properties">
            <div className="space-y-3">
              {localPlan.properties.map((p, idx) => {
                const name = (p.fields_to_apply.name as string) ?? '(unnamed)';
                const street = p.fields_to_apply.address_street as string | undefined;
                const city = p.fields_to_apply.address_city as string | undefined;
                const state = p.fields_to_apply.address_state as string | undefined;
                const zip = p.fields_to_apply.address_zip as string | undefined;
                // Edit whichever pad/unit count this property already carries so
                // a storage deal keeps writing total_units and an MHC total_lots.
                // Key presence, not value: clearing the box leaves the key behind
                // with an undefined value, and the label must not flip mid-edit.
                const lotsField =
                  'total_units' in p.fields_to_apply && !('total_lots' in p.fields_to_apply)
                    ? 'total_units'
                    : 'total_lots';
                const lots = p.fields_to_apply[lotsField] as number | undefined;
                const askingPrice = p.fields_to_apply.asking_price as number | undefined;
                const isSkipped = skipped.has(idx);

                return (
                  <div
                    key={idx}
                    className={cn(
                      'border rounded-lg p-3 transition-colors',
                      isSkipped ? 'border-gray-200 bg-gray-50 opacity-60 dark:border-gray-700 dark:bg-gray-800' : 'hover:border-primary-300',
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <PropertyMapThumbnail
                        street={street}
                        city={city}
                        state={state}
                        zip={zip}
                        className="w-24 h-16"
                      />
                      {/* Name + actions share the top row; the field block below
                          spans the card's full width, so City/State/ZIP/Lots sit
                          on one line instead of stacking in a squeezed column. */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="max-w-[16rem] flex-1 min-w-[9rem]">
                            <Input
                              value={name === '(unnamed)' ? '' : name}
                              onChange={(e) => setPropertyField(idx, 'name', e.target.value)}
                              placeholder="Property name"
                              className="h-8 py-1 font-semibold"
                            />
                          </div>
                          <Badge
                            size="sm"
                            variant={p.action === 'create' ? 'blue' : 'gray'}
                          >
                            {p.action === 'create' ? 'Create' : 'Update'}
                          </Badge>
                          {p.is_primary && (
                            <Badge size="sm" variant="primary">Primary</Badge>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {isSkipped ? (
                            <>
                              <Badge size="sm" variant="gray">Skipped</Badge>
                              <Button
                                size="sm"
                                variant="ghost"
                                leftIcon={<Undo2 className="h-3.5 w-3.5" />}
                                onClick={() =>
                                  setSkipped((prev) => {
                                    const next = new Set(prev);
                                    next.delete(idx);
                                    return next;
                                  })
                                }
                              >
                                Undo
                              </Button>
                            </>
                          ) : (
                            <>
                              {!p.is_primary && (
                                <Button size="sm" variant="ghost" onClick={() => togglePrimary(idx)}>
                                  Make primary
                                </Button>
                              )}
                              {/* Soft skip: mark excluded and dim the card. The row stays
                                  so it can be undone; the property_skip feedback and the
                                  actual removal from the plan only fire on Continue. */}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setSkipped((prev) => new Set(prev).add(idx))}
                                className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                              >
                                Skip
                              </Button>
                            </>
                          )}
                        </div>
                        </div>
                        {/* Location and pad count are editable here, not just
                            readable: when the extractor splits one park across
                            entities (a land LLC and the operating MHC), the only
                            way to reconcile them into the row worth keeping is
                            to correct these before applying. */}
                        <div className="mt-2 space-y-1.5">
                          <PlanPropertyField
                            label="Street"
                            value={street}
                            placeholder="Street address"
                            onChange={(v) => setPropertyField(idx, 'address_street', v)}
                          />
                          {/* Flex with fixed widths, not a 12-column grid: this
                              block sits in a narrow card column, and equal grid
                              tracks squeeze State/ZIP until their labels collide. */}
                          <div className="flex flex-wrap items-end gap-2">
                            <PlanPropertyField
                              label="City"
                              className="min-w-[6rem] flex-1"
                              value={city}
                              onChange={(v) => setPropertyField(idx, 'address_city', v)}
                            />
                            <PlanPropertyField
                              label="State"
                              className="w-14"
                              value={state}
                              onChange={(v) => setPropertyField(idx, 'address_state', v)}
                            />
                            <PlanPropertyField
                              label="ZIP"
                              className="w-[4.5rem]"
                              value={zip}
                              onChange={(v) => setPropertyField(idx, 'address_zip', v)}
                            />
                            <PlanPropertyField
                              label={lotsField === 'total_units' ? 'Units' : 'Lots'}
                              className="w-[4.5rem]"
                              value={lots}
                              numeric
                              onChange={(v) => setPropertyField(idx, lotsField, v)}
                            />
                          </div>
                        </div>
                        {askingPrice ? (
                          <div className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
                            ${askingPrice.toLocaleString()}
                          </div>
                        ) : null}
                        <div className="mt-1.5">
                          <PropertyStarCell
                            property={p}
                            candidate={candidateRatings[planPropertyStarKey(idx)]}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              {localPlan.properties.length === 0 && (
                <div className="text-sm text-gray-500 text-center py-6 dark:text-gray-400">
                  No properties in this plan. Add one or cancel.
                </div>
              )}
            </div>
          </TabsContent>

          {/* QUESTIONS */}
          <TabsContent value="questions">
            {questions.length === 0 ? (
              <div className="text-sm text-gray-500 flex items-center gap-2 py-6 justify-center dark:text-gray-400">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                No questions to resolve. Ready to apply.
              </div>
            ) : (
              <ul className="space-y-3">
                {questions.map((q) => (
                  <QuestionRow
                    key={q.id}
                    question={q}
                    disabled={resolveQuestion.isPending}
                    onResolve={(resolved_value) => {
                      applyAnswerToPlan(q, resolved_value);
                      resolveQuestion.mutate({ questionId: q.id, jobId, status: 'resolved', resolved_value, resolved_by: currentUserName });
                    }}
                    onDismiss={() =>
                      resolveQuestion.mutate({ questionId: q.id, jobId, status: 'dismissed', resolved_by: currentUserName })
                    }
                  />
                ))}
              </ul>
            )}
          </TabsContent>

          {/* DOCUMENTS */}
          <TabsContent value="documents">
            <ul className="divide-y divide-gray-100 border rounded-lg dark:divide-gray-800">
              {files.map((f) => (
                <li key={f.id} className="px-3 py-2 flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-900 truncate dark:text-gray-100">{f.file_name}</div>
                    {f.processing_state === 'failed' && f.extraction_errors ? (
                      <div className="text-xs text-red-600 truncate dark:text-red-400" title={f.extraction_errors}>
                        Failed: {summarizeImportError(f.extraction_errors).short}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {f.classification ?? 'unclassified'} ·{' '}
                        {typeof f.classification_confidence === 'number'
                          ? `${(f.classification_confidence * 100).toFixed(0)}% confidence`
                          : 'no confidence'}
                      </div>
                    )}
                  </div>
                  {f.processing_state === 'failed' ? (
                    <Badge size="sm" variant="red">Failed</Badge>
                  ) : !f.user_skip ? (
                    <Badge size="sm" variant="green">Include</Badge>
                  ) : (
                    <Badge size="sm" variant="outline">Skipped</Badge>
                  )}
                </li>
              ))}
            </ul>
          </TabsContent>

          {/* COMPS */}
          <TabsContent value="comps">
            {compCount === 0 ? (
              <div className="text-sm text-gray-500 text-center py-6 dark:text-gray-400">
                No comps captured in this import.
              </div>
            ) : (
              <div className="space-y-5">
                {/* Sales & financing comps */}
                {(localPlan.valuations?.length ?? 0) > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Sales &amp; financing comps ({localPlan.valuations.length})
                      </h4>
                    </div>
                    <div className="space-y-2">
                      {localPlan.valuations.map((v, idx) => {
                        const facts = [
                          fmtMoney(v.price),
                          v.cap_rate != null ? `${fmtPct(v.cap_rate)} cap` : null,
                          v.transaction_date || null,
                          v.data_source || null,
                        ].filter(Boolean);
                        return (
                          <div key={idx} className="border rounded-lg p-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {v.property_match?.suggested_name || 'Comp'}
                              </span>
                              <Badge size="sm" variant="gray">{v.transaction_type}</Badge>
                            </div>
                            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                              {facts.length ? facts.join(' · ') : '—'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Market rent comps */}
                {(localPlan.rent_comps?.length ?? 0) > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Market rent comps ({localPlan.rent_comps.length})
                      </h4>
                    </div>
                    <div className="space-y-2">
                      {localPlan.rent_comps.map((rc, idx) => {
                        const loc = [rc.address_city, rc.address_state].filter(Boolean).join(', ');
                        const facts = [
                          rc.lot_rent != null ? `${fmtMoney(rc.lot_rent)} lot rent` : null,
                          rc.market_lot_rent != null ? `${fmtMoney(rc.market_lot_rent)} market` : null,
                          rc.occupancy != null ? `${fmtPct(rc.occupancy)} occ` : null,
                          rc.unit_count != null ? `${rc.unit_count} units` : null,
                          rc.distance_miles != null ? `${rc.distance_miles} mi away` : null,
                        ].filter(Boolean);
                        return (
                          <div key={idx} className="border rounded-lg p-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {rc.community_name || rc.address_street || 'Rent comp'}
                              </span>
                              {rc.property_type && (
                                <Badge size="sm" variant="outline">{rc.property_type}</Badge>
                              )}
                            </div>
                            <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                              {[loc, ...facts].filter(Boolean).join(' · ') || '—'}
                            </div>
                            {rc.amenities && (
                              <div className="mt-1 text-xs text-gray-400 line-clamp-2 dark:text-gray-500">{rc.amenities}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* CONTACTS */}
          <TabsContent value="contacts">
            <div className="space-y-3">
              {(localPlan.contacts ?? []).map((c, idx) => (
                <div key={idx} className="border rounded-lg p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="grid grid-cols-2 gap-3 flex-1">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-400">Name</label>
                        <Input value={c.name ?? ''} onChange={(e) => setContactField(idx, 'name', e.target.value || null)} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-400">Company</label>
                        <Input value={c.organization ?? ''} onChange={(e) => setContactField(idx, 'organization', e.target.value || null)} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-400">Email</label>
                        <Input type="email" value={c.email ?? ''} onChange={(e) => setContactField(idx, 'email', e.target.value || null)} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-400">Phone</label>
                        <Input value={c.phone ?? ''} onChange={(e) => setContactField(idx, 'phone', e.target.value || null)} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1 dark:text-gray-400">Role</label>
                        <Input value={c.role ?? ''} onChange={(e) => setContactField(idx, 'role', e.target.value || null)} />
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeContact(idx)}
                      className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      Remove
                    </Button>
                  </div>
                  <div className="mt-2 text-xs text-gray-400 dark:text-gray-500">
                    Saved to Sources{c.role ? ` (${c.role})` : ''} and Contacts on apply.
                  </div>
                </div>
              ))}
              {contactCount === 0 && (
                <div className="text-sm text-gray-500 text-center py-6 dark:text-gray-400">
                  No contacts found. Brokers in the files or the sending email will appear here.
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Full-width error banner — reuses the importError style so a long Supabase
          error (e.g. a 409 body) reads on multiple lines instead of a truncated
          one-liner wedged into the footer. */}
      {continueError && (
        <div className="px-6 pt-3">
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 dark:border-red-800 dark:bg-red-900/30">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0 dark:text-red-400" />
            <div className="text-sm text-red-800 dark:text-red-300">{continueError}</div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between dark:border-gray-700 dark:bg-gray-800">
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {blockedByQuestions
            ? `${pendingQuestions.length} question${pendingQuestions.length === 1 ? '' : 's'} must be resolved or dismissed before continuing.`
            : 'All questions resolved. Continue to choose what to do with this import.'}
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
            onClick={() => setShowDiscardConfirm(true)}
            disabled={cancelJob.isPending || savingAndClosing || updatePlan.isPending}
            isLoading={cancelJob.isPending}
          >
            Discard import
          </Button>
          <Button
            variant="ghost"
            onClick={handleSaveAndClose}
            disabled={savingAndClosing || updatePlan.isPending || cancelJob.isPending}
            isLoading={savingAndClosing}
          >
            Save & Close
          </Button>
          <Button
            onClick={handleContinue}
            disabled={blockedByQuestions || updatePlan.isPending || savingAndClosing || cancelJob.isPending}
            isLoading={updatePlan.isPending}
          >
            Continue
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={showDiscardConfirm}
        onOpenChange={setShowDiscardConfirm}
        title="Discard import?"
        description={`Discard "${localPlan.deal?.name || 'this import'}"? It will be marked cancelled and not applied. This cannot be undone from here.`}
        confirmLabel="Discard import"
        variant="danger"
        loading={cancelJob.isPending}
        onConfirm={handleDiscard}
      />
    </div>
  );
}
